/**
 * V1.42.13 · Weekday hint global.
 *
 * Muestra el día de la semana (es-ES, minúscula inicial mayúscula) DEBAJO de
 * cualquier `<input type="date">` de la app, sin tocar ni un solo formulario.
 *
 * - Se importa una única vez en `main.jsx` (efecto global).
 * - Usa `MutationObserver` para cubrir inputs añadidos dinámicamente.
 * - Actualiza en `input`, `change` y `blur` (fecha manual + selector).
 * - Si no hay valor válido → no muestra nada (span invisible).
 * - Estilo: text-[11px] text-neutral-500 mt-0.5 select-none (gris suave, pequeño).
 * - No modifica estilos existentes: el span se inserta como sibling
 *   `nextSibling` con clase única `.weekday-hint`.
 */

const CLASS = "weekday-hint";
const FMT = new Intl.DateTimeFormat("es-ES", { weekday: "long" });
const HINTS = new WeakMap(); // input -> span

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

function computeText(value) {
  if (!value) return "";
  // Los inputs date devuelven YYYY-MM-DD (ISO). Construimos Date en local
  // para evitar corrimiento por UTC en zona +02:00.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  return cap(FMT.format(d));
}

function ensureHint(input) {
  if (!(input instanceof HTMLInputElement) || input.type !== "date") return;
  if (input.dataset.wkHintBound === "1") {
    // ya enlazado → sólo refrescar texto
    const hint = HINTS.get(input) || input.nextElementSibling;
    if (hint && hint.classList?.contains(CLASS)) hint.textContent = computeText(input.value);
    return;
  }
  const hint = document.createElement("span");
  hint.setAttribute("aria-hidden", "true");
  hint.textContent = computeText(input.value);
  // V1.53.2 · Si el input tiene un <label> hermano (patrón label+input),
  // el día va EN LÍNEA junto a la etiqueta ("Fecha * Martes"), en gris claro
  // y pequeño para no sobresalir. Si no, se mantiene debajo del input.
  const label = input.parentNode?.querySelector?.(":scope > label");
  if (label) {
    hint.className = `${CLASS} text-[11px] text-neutral-400 font-normal ml-1.5 select-none`;
    label.appendChild(hint);
  } else {
    hint.className = `${CLASS} block text-[11px] text-neutral-500 mt-0.5 select-none`;
    if (input.parentNode) input.parentNode.insertBefore(hint, input.nextSibling);
  }
  HINTS.set(input, hint);
  const refresh = () => { hint.textContent = computeText(input.value); };
  input.addEventListener("input", refresh);
  input.addEventListener("change", refresh);
  input.addEventListener("blur", refresh);
  input.dataset.wkHintBound = "1";
}

function scanAll(root = document) {
  root.querySelectorAll?.("input[type='date']").forEach(ensureHint);
}

export default function installWeekdayHint() {
  if (typeof window === "undefined" || window.__wkHintInstalled) return;
  window.__wkHintInstalled = true;
  const boot = () => {
    scanAll(document);
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes?.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.("input[type='date']")) ensureHint(n);
          else scanAll(n);
        });
        // input abierto luego rellenado programáticamente
        if (m.type === "attributes" && m.target instanceof HTMLInputElement && m.target.type === "date") {
          const hint = HINTS.get(m.target) || m.target.nextElementSibling;
          if (hint && hint.classList?.contains(CLASS)) hint.textContent = computeText(m.target.value);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["value"] });
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
}
